import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandEmpty,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Input,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Separator,
  Text,
  useIsMobile,
  useWidgetViewState,
} from "@salestrak/ui";
import { getDefaultValueByType } from "@salestrak/custom-fields";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useAvailableCustomFields } from "#app/hooks/issue-view/use-available-custom-fields";
import { useIssueViewCapabilities } from "#app/components/issue-view/issue-view-capabilities";
import type { CustomFieldDef } from "#app/components/issue-view/issue-view-types";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";
import type { Omittable } from "@salestrak/ui";

/** Fraction of the viewport the mobile drawer settles at — Drawer snap points
 *  are numeric fractions, not percentage strings. */
const ADD_FIELD_SNAP_POINTS = [0.92];

function getAttachedFieldIds({
  values,
  orderedTypeFieldIds,
}: Omittable<{
  values?: Record<string, unknown>;
  orderedTypeFieldIds?: string[];
}>) {
  const valueKeys = Object.keys(values ?? {}).filter((id) => id !== ".");
  if (!orderedTypeFieldIds?.length) return valueKeys;
  const valueKeySet = new Set(valueKeys);
  const ordered = orderedTypeFieldIds.filter((id) => valueKeySet.has(id));
  const orderedSet = new Set(ordered);
  return [...ordered, ...valueKeys.filter((id) => !orderedSet.has(id))];
}

export function IssueCustomFieldsHeaderActions() {
  const ctx = useIssueWidgetContext();
  const { state, patch } = useWidgetViewState();
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const canManageCustomFields = usePermission("custom_fields.manage");
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { canEdit } = useIssueViewCapabilities();
  const readOnly = !isCreateMode && !canEdit;
  const isMobile = useIsMobile();
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const [searchVisible, setSearchVisible] = useState(false);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  // The picker's own query. Base UI's Autocomplete filters against `items`, and
  // this list is grouped + checkbox-rendered, so the filter stays explicit here
  // (`mode="none"`) over each field's name, key, type and type label.
  const [pickerQuery, setPickerQuery] = useState("");
  const searchValue = typeof state.search === "string" ? state.search : "";
  const fieldIds = useMemo(
    () =>
      getAttachedFieldIds({
        values: ctx.values,
        orderedTypeFieldIds: ctx.orderedTypeFieldIds,
      }),
    [ctx.values, ctx.orderedTypeFieldIds],
  );
  const hasFields = fieldIds.length > 0;
  const customFieldsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const field of ctx.customFields ?? []) {
      map[field.customFieldId] = field;
    }
    return map;
  }, [ctx.customFields]);
  const allAvailableFields = useAvailableCustomFields(fieldIds);
  const availableFields = useMemo(() => {
    const needle = pickerQuery.trim().toLowerCase();
    if (!needle) return allAvailableFields;
    return allAvailableFields.filter((field) =>
      [field.name, field.key ?? "", field.type, getFieldTypeMeta(field.type).label]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allAvailableFields, pickerQuery]);

  // Focus the picker input only once the drawer finishes its open animation.
  // Focusing mid-animation opens the virtual keyboard, which resizes the visual
  // viewport and desyncs the drawer's translate math.
  const handleDrawerSettled = useCallback((isOpen: boolean) => {
    if (isOpen) pickerInputRef.current?.focus();
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      patch({ search: value });
    },
    [patch],
  );

  const handleCloseSearch = useCallback(() => {
    patch({ search: "" });
    setSearchVisible(false);
  }, [patch]);

  const handleNavigateToCreateField = useCallback(() => {
    setAddFieldDialogOpen(false);
    openCustomFieldsDialog();
  }, [openCustomFieldsDialog]);

  const handleOpenAddFieldDialog = useCallback(() => {
    setSelectedFieldIds([]);
    setPickerQuery("");
    setAddFieldDialogOpen(true);
  }, []);

  const handlePickerOpenChange = useCallback((open: boolean) => {
    setAddFieldDialogOpen(open);
    if (!open) {
      setSelectedFieldIds([]);
      setPickerQuery("");
    }
  }, []);

  const handleAddFields = useCallback(() => {
    for (const cfId of selectedFieldIds) {
      const defaultValue = getDefaultValueByType(customFieldsById[cfId]?.type);
      ctx.onUpdateValue?.({ customFieldId: cfId, value: defaultValue });
    }
    setAddFieldDialogOpen(false);
    setSelectedFieldIds([]);
  }, [selectedFieldIds, customFieldsById, ctx]);

  const toggleFieldSelection = useCallback((cfId: string) => {
    setSelectedFieldIds((prev) =>
      prev.includes(cfId) ? prev.filter((id) => id !== cfId) : [...prev, cfId],
    );
  }, []);

  const addFieldItems = (
    <>
      <CommandGroup>
        <CommandGroupLabel className="normal-case tracking-normal">
          {`${availableFields.length} Result${availableFields.length !== 1 ? "s" : ""}`}
        </CommandGroupLabel>
        {availableFields.map((field) => {
          const isSelected = selectedFieldIds.includes(field.customFieldId);
          const meta = getFieldTypeMeta(field.type);
          const desc = [
            field.key,
            field.createdBy?.name ? `Created by ${field.createdBy.name}` : null,
            !isMobile ? formatRelativeDate(field.creationDate) : null,
          ]
            .filter(Boolean)
            .join("  ·  ");
          return (
            <CommandItem
              key={field.customFieldId}
              value={field.customFieldId}
              onClick={() => toggleFieldSelection(field.customFieldId)}
            >
              {/* The row owns the press; the box is a state glyph, not a
                * separate hit target — hence `pointer-events-none` on it. */}
              <Box direction="row" align="center" gap="sm" fill="width" minWidth={0}>
                <Checkbox checked={isSelected} className="pointer-events-none" />
                <Icon name={meta.icon} size="md" color={meta.color} />
                <Box direction="column" grow gap="xs" overflow="hidden" minWidth={0}>
                  <Text size="sm" weight="medium" truncate={1}>
                    {field.name}
                  </Text>
                  {desc ? (
                    <Text size="xs" variant="muted" truncate={1}>
                      {desc}
                    </Text>
                  ) : null}
                </Box>
              </Box>
            </CommandItem>
          );
        })}
      </CommandGroup>
      <CommandEmpty>No fields found.</CommandEmpty>
    </>
  );

  /** The shared picker body — one Command tree for both surfaces. */
  const pickerCommand = (
    <Command mode="none" onValueChange={setPickerQuery} value={pickerQuery}>
      <CommandInput
        ref={pickerInputRef}
        autoFocus={!isMobile}
        placeholder="Search custom fields..."
      />
      {/* grow + min-height:0 hands the ScrollArea inside CommandList a definite
          height, so the list scrolls instead of pushing the surface. */}
      <Box direction="column" grow minHeight={0}>
        <CommandList>{addFieldItems}</CommandList>
      </Box>
    </Command>
  );

  return (
    <Box direction="row" align="center" gap="xs">
      {hasFields && !searchVisible ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Search fields"
          onClick={() => setSearchVisible(true)}
        >
          <Icon name="search" size="md" />
        </Button>
      ) : null}
      {searchVisible ? (
        <Box direction="row" align="center" gap="xs">
          <Box direction="row" align="center" width={180}>
            <Input
              value={searchValue}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Search..."
              size="sm"
              autoFocus
            />
          </Box>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close search"
            onClick={handleCloseSearch}
          >
            <Icon name="x" size="md" />
          </Button>
        </Box>
      ) : null}
      {!isCreateMode && !readOnly ? (
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-sm" aria-label="Add a field" />
            }
          >
            <Icon name="plus" size="md" />
          </MenuTrigger>
          <MenuPopup align="end">
            {canManageCustomFields ? (
              <MenuItem onClick={handleNavigateToCreateField}>
                <Icon name="plus" size="md" />
                <Text>Create field...</Text>
              </MenuItem>
            ) : null}
            <MenuItem onClick={handleOpenAddFieldDialog}>
              <Icon name="book-open" size="md" />
              <Text>Add field from workspace...</Text>
            </MenuItem>
          </MenuPopup>
        </Menu>
      ) : null}

      {isMobile ? (
        // Mobile gets a drawer of its own rather than a `bottomStickOnMobile`
        // dialog: the picker is a near-full-height, snap-pointed surface whose
        // search input must not focus mid-animation. Same shape as the
        // global-search drawer. `DrawerFooter` pays the safe-area inset itself.
        <Drawer
          open={addFieldDialogOpen}
          onOpenChange={handlePickerOpenChange}
          onOpenChangeComplete={handleDrawerSettled}
          snapPoints={ADD_FIELD_SNAP_POINTS}
        >
          <DrawerPopup showBar>
            <DrawerHeader>
              <Box direction="row" align="center" justify="between" gap="xs">
                <DrawerTitle>Add custom fields</DrawerTitle>
                <Box direction="row" align="center" gap="xs" shrink={false}>
                  {canManageCustomFields ? (
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      aria-label="Manage custom fields"
                      onClick={handleNavigateToCreateField}
                    >
                      <Icon name="settings" />
                    </Button>
                  ) : null}
                  <Button
                    size="icon"
                    className="rounded-full"
                    aria-label="Add selected fields"
                    onClick={handleAddFields}
                    disabled={selectedFieldIds.length === 0}
                  >
                    <Icon name="check" />
                  </Button>
                </Box>
              </Box>
            </DrawerHeader>
            {pickerCommand}
            <DrawerFooter variant="bare">
              {selectedFieldIds.length > 0 ? (
                <Text size="sm" variant="muted">
                  {selectedFieldIds.length} selected
                </Text>
              ) : null}
            </DrawerFooter>
          </DrawerPopup>
        </Drawer>
      ) : (
        <CommandDialog open={addFieldDialogOpen} onOpenChange={handlePickerOpenChange}>
          {/* The popup has no size prop; its width is a sizing className. */}
          <CommandDialogPopup aria-label="Add custom fields" className="sm:max-w-[500px]">
            {pickerCommand}
            <Separator />
            <Box direction="row" align="center" justify="between" padding="md">
              {canManageCustomFields ? (
                <Button variant="ghost" size="sm" onClick={handleNavigateToCreateField}>
                  <Text size="sm" variant="muted">
                    Manage fields
                  </Text>
                </Button>
              ) : (
                <Box />
              )}
              <Box direction="row" align="center" gap="sm">
                {selectedFieldIds.length > 0 ? (
                  <Text size="sm" variant="muted">
                    {selectedFieldIds.length} selected
                  </Text>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePickerOpenChange(false)}
                >
                  <Text>Cancel</Text>
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddFields}
                  disabled={selectedFieldIds.length === 0}
                >
                  <Text>Add fields</Text>
                </Button>
              </Box>
            </Box>
          </CommandDialogPopup>
        </CommandDialog>
      )}
    </Box>
  );
}
